import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, message, Modal, Select, Tag, Form, DatePicker } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Edit, Trash2 } from 'lucide-react';
import ModalWrapper from '../../../../Modal';
import { getRequest, deleteRequest, postRequest, putRequest } from '../../../../service';
import dayjs from 'dayjs';
import OffboardingForm from '../../../../modals/addOffboarding';
import { useNavigate } from 'react-router-dom';
import { getEmployeeData } from '../../../../utils/localStorage';
import { formatDate } from '../../../../utils/dateTime';

const { Search } = Input;

const Offboarding = () => {
  const [offboardingData, setOffboardingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teamInfo, setTeamInfo] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Permission checks
  const employeeData = getEmployeeData();
  const userRole = employeeData?.role;
  const offboardingPermissions = employeeData?.designation?.permissions?.Employee?.Offboarding || [];

  const canView = userRole === "companyadmin" || offboardingPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || offboardingPermissions.includes("add");
  const canEdit = userRole === "companyadmin" || offboardingPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || offboardingPermissions.includes("delete");
  console.log("employeeList",employeeList);
  const fetchOffboardingData = async () => {
    setLoading(true);
    try {
      if (!canView) {
        setOffboardingData([]);
      } else {
        const response = await getRequest('/api/offboarding?page=1&limit=10');
        if (response?.data && Array.isArray(response.data)) {
          setOffboardingData(response.data);
        }
      }
    } catch (error) {
      console.error('Error fetching offboarding data:', error);
      message.error('Failed to fetch offboarding data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch offboarding data from API
  useEffect(() => {
    fetchOffboardingData();
  }, [canView]);

  // Fetch employees from API
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await getRequest('/api/companies/reportto');
        if (response?.employees) {
          setEmployees(response.employees);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
        message.error('Failed to fetch employees');
      }
    };
    fetchEmployees();
  }, []);

  // Watch selected employee and fetch team members when it changes
  const selectedEmployeeId = Form.useWatch('employeeIds', form);
  useEffect(() => {
    const fetchEmployeesList = async () => {
      if (!selectedEmployeeId) {
        setEmployeeList([]);
        setTeamInfo(null);
        return;
      }
      try {
        const response = await getRequest(`/api/companies/teammembers?employeeId=${selectedEmployeeId}`);
        setTeamInfo(response || null);
        if (response?.employee) {
          setEmployeeList(response);
        } else {
          setEmployeeList([]);
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
        setEmployeeList([]);
        setTeamInfo(null);
      }
    };
    fetchEmployeesList();
  }, [selectedEmployeeId]);

  const handleSubmit = async (values) => {
    if (!canAdd) {
      message.warning("You don't have permission to add offboarding requests.");
      return;
    }

    try {
      const payload = {
        employeeId: values.employeeIds,
        resignationDate: values.resignDate ? values.resignDate.format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
        status: values.status,
        lastWorkingDate: values.lastWorkingDate.format('YYYY-MM-DD'),
        noticePeriod: parseInt(values.noOfDays) || 0,
        reason: values.reason,
        statusResign: "accept"
      };

      const response = await postRequest('/api/offboarding', payload);

      if (response.message === 'Offboarding record created successfully') {
        const managementIds = (teamInfo?.managementChain || []).map(m => m?._id).filter(Boolean);
        const selectedIds = Array.isArray(values.employeeIds) ? values.employeeIds : [values.employeeIds];
        const uniqueReceiverIds = Array.from(new Set([...selectedIds, ...managementIds].filter(Boolean)));
        const res = await postRequest('/api/notifications', {
          "title": `${employeeList.employee.name} ${values.status}`,
          "senderId": employeeData?.userId,
          "receiverIds": uniqueReceiverIds,
          "type": "Offboarding",
          "relatedTypeId": response._id,
          "relatedDocumentModel": "Offboarding",
          "companyId": employeeData?.companyId,
          "priority": "high"
        });
        message.success('Offboarding request submitted successfully');
        setAddModalOpen(false);
        form.resetFields();
        fetchOffboardingData();
      } else {
        message.error('Failed to submit offboarding request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting offboarding:', error);
      message.error('Failed to submit offboarding request. Please try again.');
    }
  };



  const handleEditSubmit = async (values) => {
    if (!canEdit) {
      message.warning("You don't have permission to edit offboarding requests.");
      return;
    }

    try {
      const payload = {
        employeeId: values.employeeIds,
        resignationDate: values.resignDate ? values.resignDate.format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
        status: values.status,
        lastWorkingDate: values.lastWorkingDate.format('YYYY-MM-DD'),
        noticePeriod: parseInt(values.noOfDays) || 0,
        reason: values.reason,
      };

      const response = await putRequest(`/api/offboarding/${selectedRecord._id}`, payload);

      if (response.message === 'Offboarding record updated successfully' || response.success) {
        message.success('Offboarding record updated successfully');
        setEditModalOpen(false);
        setSelectedRecord(null);
        form.resetFields();
        fetchOffboardingData();
      } else {
        message.error('Failed to update offboarding record. Please try again.');
      }
    } catch (error) {
      console.error('Error updating offboarding record:', error);
      message.error('Failed to update offboarding record. Please try again.');
    }
  };

    const handleStatusUpdate = async (statusResign, comments) => {
    try {
      const payload = {
        ...selectedRecord,
        statusResign: statusResign,
        comments: comments
      };

      const response = await putRequest(`/api/offboarding/${selectedRecord._id}`, payload);
      
      if (response.message === 'Offboarding record updated successfully' || response.success) {
        message.success(`Offboarding request ${statusResign}ed successfully`);
        setEditModalOpen(false);
        setSelectedRecord(null);
        form.resetFields();
        fetchOffboardingData();
      } else {
        message.error(`Failed to ${statusResign} offboarding request. Please try again.`);
      }
    } catch (error) {
      console.error(`Error ${statusResign}ing offboarding request:`, error);
      message.error(`Failed to ${statusResign} offboarding request. Please try again.`);
    }
  };

  const handleEdit = (record) => {
    if (!canEdit) {
      message.warning("You don't have permission to edit offboarding requests.");
      return;
    }

    // Prevent editing accepted requests
    if (record.statusResign === "accept") {
      message.warning("Cannot edit accepted offboarding requests");
      return;
    }

    setSelectedRecord(record);
    setEditModalOpen(true);

    form.setFieldsValue({
      employeeIds: record.employeeId && Array.isArray(record.employeeId)
        ? record.employeeId[0]?._id
        : (record.employeeId?._id),
      status: record.status,
      resignDate: record.resignationDate ? dayjs(record.resignationDate) : null,
      lastWorkingDate: record.lastWorkingDate ? dayjs(record.lastWorkingDate) : null,
      noOfDays: record.noticePeriod,
      reason: record.reason,
      comments: record.comments || ''
    });
  };

  const handleDelete = (record) => {
    if (!canDelete) {
      message.warning("You don't have permission to delete offboarding requests.");
      return;
    }

    // Prevent deleting accepted requests (company admin can always delete)
    if (record.statusResign === "accept" && userRole !== "companyadmin") {
      message.warning("Cannot delete accepted offboarding requests");
      return;
    }

    Modal.confirm({
      title: 'Are you sure you want to delete this offboarding record?',
      content: `This will permanently delete the offboarding record for "${record.employeeId?.[0]?.firstName} ${record.employeeId?.[0]?.lastName}".`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        await deleteRequest(`/api/offboarding/${record._id}`);
        message.success('Offboarding record deleted successfully');
        fetchOffboardingData();
      },
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resigned':
        return 'blue';
      case 'Termination':
        return 'red';
      case 'LayOff':
        return 'orange';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: "Employee",
      key: "employee",
      render: (_, record) => (
        <div>
          <div className="font-medium">
            {record.employeeId?.[0]?.firstName} {record.employeeId?.[0]?.lastName}
          </div>
          <div className="text-sm text-gray-500">{record.employeeId?.[0]?.employee_code}</div>
        </div>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (_, record) => (
        <div className="text-sm text-gray-600">
          {record.employeeId?.[0]?.emailAddress}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
      ),
    },
    {
      title: "Resignation Status",
      key: "statusResign",
      render: (_, record) => (
        <Tag color={getStatusColor(record.statusResign)}>{record.statusResign?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Resignation Date",
      key: "resignationDate",
      render: (_, record) => (
        <div className="text-sm">
          {record.resignationDate ? formatDate(record.resignationDate) : 'N/A'}
        </div>
      ),
    },
    {
      title: "Last Working Date",
      key: "lastWorkingDate",
      render: (_, record) => (
        <div className="text-sm">
          {record.lastWorkingDate ? formatDate(record.lastWorkingDate) : 'N/A'}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isAccepted = record.statusResign === "accept";
        const canEditRecord = canEdit && !isAccepted;
        const canDeleteRecord = canDelete && (userRole === "companyadmin" || !isAccepted);
        
        return (
          <Space>
            <Button
              type="text"
              icon={<Edit className="h-4 w-4" />}
              onClick={() => handleEdit(record)}
              className={`${!canEditRecord ? 'text-gray-400 cursor-not-allowed' : 'text-green-600 hover:text-green-800'}`}
              title={!canEdit ? "You don't have permission to edit" : isAccepted ? "Cannot edit accepted requests" : "Edit"}
              disabled={!canEditRecord}
            />
            <Button
              type="text"
              icon={<Trash2 className="h-4 w-4" />}
              onClick={() => handleDelete(record)}
              className={`${!canDeleteRecord ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'}`}
              title={!canDelete ? "You don't have permission to delete" : isAccepted && userRole !== "companyadmin" ? "Cannot delete accepted requests" : "Delete"}
              disabled={!canDeleteRecord}
            />
          </Space>
        );
      },
    },
  ];

  // Filter data based on search
  const filteredData = offboardingData.filter(record => {
    const employeeName = `${record.employeeId?.[0]?.firstName || ''} ${record.employeeId?.[0]?.lastName || ''}`.toLowerCase();
    const employeeCode = record.employeeId?.[0]?.employee_code?.toLowerCase() || '';
    const email = record.employeeId?.[0]?.emailAddress?.toLowerCase() || '';

    const matchesSearch = employeeName.includes(searchText.toLowerCase()) ||
      employeeCode.includes(searchText.toLowerCase()) ||
      email.includes(searchText.toLowerCase());

    return matchesSearch;
  });

  return (
    <div className="p-6">
      <div className="w-full">
        <h4 className="text-2xl font-semibold mb-6">Employee Offboarding</h4>

        {/* Action Button */}
        <div className="flex justify-between items-center mb-4">
          <div className="w-full max-w-md">
            <Search
              placeholder="Search by employee name or ID"
              allowClear
              className="w-full"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => setSearchText(value)}
            />
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="bg-primary text-white hover:bg-primary"
            disabled={!canAdd}
            onClick={() => canAdd ? setAddModalOpen(true) : message.warning("You don't have permission to add offboarding requests.")}
          >
            Add Offboarding Request
          </Button>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={canView ? filteredData : []}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            total: canView ? filteredData.length : 0,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} records`,
            showQuickJumper: true,
          }}
          className="bg-white rounded-lg border border-gray-200"
        />
        {!canView && (
          <div className="text-center text-gray-500 py-4">You don't have permission to view offboarding requests.</div>
        )}
      </div>

      {/* Add/Edit Offboarding Modal */}
      <ModalWrapper
        open={addModalOpen || editModalOpen}
        onRequestClose={() => {
          setAddModalOpen(false);
          setEditModalOpen(false);
          setSelectedRecord(null);
          form.resetFields();
        }}
        title={editModalOpen ? "Edit Offboarding Request" : "Add Offboarding Request"}
      >
        <OffboardingForm
          form={form}
          onSubmit={editModalOpen ? handleEditSubmit : handleSubmit}
          initialValues={selectedRecord}
          isEdit={editModalOpen}
          employees={employees}
          onStatusUpdate={handleStatusUpdate}
        />
      </ModalWrapper>
    </div>
  );
};
export default Offboarding;
